import {
  CustomMissingTranslationHandler,
  dynamicLocaleFactory,
  httpLoaderFactory,
} from '$core/helpers/ngx-translate';
import { CustomRouteReuseStrategy } from '$core/helpers/route-reuse-strategy';
import { ConfigService } from '$core/services/config.service';
import { DayjsConfigService } from '$core/services/dayjs-config.service';
import { LanguageService } from '$core/services/language.service';
import { provideGitHash } from '$core/util/build-info';
import { environment } from '$env/environment';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeBe from '@angular/common/locales/be';
import localeEn from '@angular/common/locales/en';
import localeRu from '@angular/common/locales/ru';
import {
  APP_INITIALIZER,
  Injectable,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import { AbstractControlDirective } from '@angular/forms';
import {
  BrowserModule,
  HammerGestureConfig,
  HAMMER_GESTURE_CONFIG,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GIT_HASH } from './build-info';
import { CoreModule } from './core/core.module';

registerLocaleData(localeRu, 'ru');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeBe, 'by');

export function initSettings(settings: ConfigService) {
  return () => settings.loadConfig();
}

@Injectable()
export class MyHammerConfig extends HammerGestureConfig {
  public buildHammer(element: HTMLElement) {
    const mc = new Hammer(element, {
      touchAction: 'pan-y',
    });
    return mc;
  }
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ScrollingModule,
    CoreModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
      },
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
      useDefaultLang: true,
    }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.serviceWorker,
    }),
    GoogleTagManagerModule.forRoot({
      id: environment.gtag ? (<any>environment.gtag).site : null,
    }),
  ],
  providers: [
    provideGitHash(GIT_HASH),
    {
      provide: LOCALE_ID,
      useFactory: dynamicLocaleFactory,
      deps: [LanguageService],
    },
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MyHammerConfig,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initSettings,
      deps: [ConfigService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private dayjsConfig: DayjsConfigService,
    _trace: Sentry.TraceService
  ) {
    /* Monkey patch */
    Object.defineProperty(AbstractControlDirective.prototype, 'errors', {
      get: function () {
        return this.control ? this.control.errors : null;
      },
      set: function (value) {
        this.control.errors = value;
      },
    });

    if (!environment.serviceWorker && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (const registration of registrations) {
          console.log('serviceWorker unregistered');
          registration.unregister();
        }
      });
    }
  }
}
